import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface Lead {
  id: number;
  name: string;
  industry_id: number;
  full_address: string;
  phone: string;
  email: string;
  website: string;
  distance_from_so16: number;
  status: string;
}

interface LeadStatus {
  id: number;
  lead_id: number;
  status: string;
  notes: string;
  contacted_date: string;
  follow_up_date: string;
}

const LeadManagement: React.FC = () => {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    notes: '',
    contacted_date: '',
    follow_up_date: ''
  });
  const queryClient = useQueryClient();

  // Fetch leads
  const { data: leads, isLoading: leadsLoading } = useQuery({
    queryKey: ['leads'],
    queryFn: async () => {
      const response = await axios.get('/api/leads/read.php');
      return response.data;
    }
  });

  // Fetch lead status
  const { data: leadStatuses, isLoading: statusLoading } = useQuery({
    queryKey: ['leadStatuses'],
    queryFn: async () => {
      const response = await axios.get('/api/lead_status/read.php');
      return response.data;
    }
  });

  // Update lead status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (data: { lead_id: number; status: string; notes: string; contacted_date: string; follow_up_date: string }) => {
      const response = await axios.post('/api/lead_status/update.php', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadStatuses'] });
      setStatusUpdate({
        status: '',
        notes: '',
        contacted_date: '',
        follow_up_date: ''
      });
    }
  });

  const handleStatusUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedLead) {
      updateStatusMutation.mutate({
        lead_id: selectedLead.id,
        ...statusUpdate
      });
    }
  };

  if (leadsLoading || statusLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Lead Management</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Leads List */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-2">Leads</h2>
          <div className="space-y-2">
            {leads?.map((lead: Lead) => (
              <div
                key={lead.id}
                className={`p-3 rounded cursor-pointer ${
                  selectedLead?.id === lead.id
                    ? 'bg-blue-100 border-blue-500'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                onClick={() => setSelectedLead(lead)}
              >
                <h3 className="font-medium">{lead.name}</h3>
                <p className="text-sm text-gray-600">{lead.full_address}</p>
                <p className="text-sm text-gray-600">{lead.phone}</p>
                <p className="text-sm text-gray-600">{lead.email}</p>
                <p className="text-sm text-gray-600">
                  Distance: {lead.distance_from_so16} km
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Status Update Form */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-2">Update Status</h2>
          {selectedLead ? (
            <form onSubmit={handleStatusUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={statusUpdate.status}
                  onChange={(e) =>
                    setStatusUpdate({ ...statusUpdate, status: e.target.value })
                  }
                >
                  <option value="">Select Status</option>
                  <option value="new">New</option>
                  <option value="email_sent">Email Sent</option>
                  <option value="contacted_in_person">Contacted in Person</option>
                  <option value="waiting">Waiting</option>
                  <option value="followed_up">Followed Up</option>
                  <option value="inside_trial">Inside Trial</option>
                  <option value="subscribed">Subscribed</option>
                  <option value="not_interested">Not Interested</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <textarea
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  rows={3}
                  value={statusUpdate.notes}
                  onChange={(e) =>
                    setStatusUpdate({ ...statusUpdate, notes: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Contacted Date
                </label>
                <input
                  type="datetime-local"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={statusUpdate.contacted_date}
                  onChange={(e) =>
                    setStatusUpdate({
                      ...statusUpdate,
                      contacted_date: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Follow-up Date
                </label>
                <input
                  type="datetime-local"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={statusUpdate.follow_up_date}
                  onChange={(e) =>
                    setStatusUpdate({
                      ...statusUpdate,
                      follow_up_date: e.target.value,
                    })
                  }
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                disabled={updateStatusMutation.isPending}
              >
                {updateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
              </button>
            </form>
          ) : (
            <p className="text-gray-500">Select a lead to update its status</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadManagement; 